import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { getStaffRequirement, resolveLine, publishQuote, addStaffComment, setCustomerStatus, setWaitingOn } from "../../services/requirementsService";
import { errorToast, successToast } from "../Toasts/Toasts";
import SourcingPanel from "./SourcingPanel";
import VendorCoveragePanel from "./VendorCoveragePanel";
import CrmAccountLink from "./CrmAccountLink";
import { WAITING_ON, WaitingBadge } from "./WaitingOn";

// CMS: one requirement, priced. Every line with its match, cost, sell,
// margin and flags; pick a product/variant for the ambiguous ones, set a
// sell rate or HSN/GST by hand, publish the quotation, talk to the customer.

const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const when = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const FLAG_CLASS = { LOSS: "danger", LOW_MARGIN: "warning text-dark", STALE_COST: "secondary", UNCERTAIN_MAPPING: "warning text-dark", NO_COST: "danger", NO_TAX_RATE: "danger", FIXED_PRICE: "info text-dark", TRANSPORT_HEAVY: "warning text-dark" };

const LineRow = ({ line, number, onChanged }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ sellRateOverride: line.sellRateOverride ?? "", hsnCode: line.hsnCode ?? "", gstPct: line.gstPct ?? "", variantId: "", productId: "", isSeparateCharge: line.isSeparateCharge });
  const cands = line.matchCandidates || {};

  const save = async () => {
    const body = {};
    if (form.variantId) body.variantId = form.variantId;
    else if (form.productId) body.productId = form.productId;
    if (String(form.sellRateOverride) !== String(line.sellRateOverride ?? "")) body.sellRateOverride = form.sellRateOverride === "" ? null : Number(form.sellRateOverride);
    if (String(form.hsnCode) !== String(line.hsnCode ?? "") || String(form.gstPct) !== String(line.gstPct ?? "")) { body.hsnCode = form.hsnCode || null; body.gstPct = form.gstPct === "" ? null : Number(form.gstPct); }
    if (form.isSeparateCharge !== line.isSeparateCharge) body.isSeparateCharge = form.isSeparateCharge;
    if (!Object.keys(body).length) return setEdit(false);
    try {
      const r = await resolveLine(number, line.id, body);
      if (r?.success) { setEdit(false); onChanged(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); }
  };

  return (
    <>
      <tr>
        <td>{line.lineNo}</td>
        <td>
          <div>{line.requestedText}</div>
          <div className="small text-muted">
            {line.matchStatus === "matched" || line.matchStatus === "resolved" ? <>→ {line.zohoVariantName || line.zohoProductName}{line.zohoProductId ? <Link className="ms-2" to={`/catalogue/products/${line.zohoProductId}`}>catalogue</Link> : null}</> : <span className="text-warning">{line.matchStatus}{line.matchNote ? ` — ${line.matchNote}` : ""}{line.zohoProductId ? <Link className="ms-2" to={`/catalogue/products/${line.zohoProductId}`}>add size</Link> : null}</span>}
            {line.matchScore !== null && line.matchScore !== undefined && line.matchScore < 95 ? <span className="badge bg-warning text-dark ms-2" title="How the match was reached - under 95 means it was not an exact name or size">match {line.matchScore}</span> : null}
          </div>
        </td>
        <td className="text-end">{line.quantity ? `${Number(line.quantity)} ${line.unit || ""}` : ""}</td>
        <td className="text-end">{line.costRate ? <>{inr(line.costRate)}{Number(line.transportAllocation) > 0 ? <span className="small text-muted"> +{inr(line.transportAllocation)} {line.laneCity || "transport"}</span> : null}<div className="small text-muted">{line.costVendorName}<br />{line.costRateDate}</div></> : "—"}</td>
        <td className="text-end">{inr(line.sellRate)}{line.sellRateOverride !== null && line.sellRateOverride !== undefined ? <div className="small text-muted">override</div> : null}</td>
        <td className="text-end">{inr(line.sellAmount)}</td>
        <td className="text-end">{line.marginPct !== null && line.marginPct !== undefined ? `${Number(line.marginPct)}%` : "—"}</td>
        <td className="small">{line.hsnCode || "—"}<br />{line.gstPct !== null && line.gstPct !== undefined ? `${Number(line.gstPct)}%` : ""}</td>
        <td>{(line.flags || []).map((f) => <span key={f} className={`badge bg-${FLAG_CLASS[f] || "secondary"} me-1`}>{f}</span>)}</td>
        <td><Button size="sm" variant="outline-secondary" onClick={() => setEdit(!edit)}>{edit ? "Close" : "Edit"}</Button></td>
      </tr>
      {edit && (
        <tr className="table-light">
          <td colSpan={10}>
            <div className="row g-2 align-items-end">
              {cands.products?.length > 0 && (
                <div className="col-md-4">
                  <Form.Label className="small mb-0">Product</Form.Label>
                  <Form.Select size="sm" value={form.productId} onChange={(e) => setForm({ ...form, productId: e.target.value, variantId: "" })}>
                    <option value="">— pick —</option>
                    {cands.products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.scientificName ? ` (${p.scientificName})` : ""}</option>)}
                  </Form.Select>
                </div>
              )}
              {cands.variants?.length > 0 && (
                <div className="col-md-4">
                  <Form.Label className="small mb-0">Variant</Form.Label>
                  <Form.Select size="sm" value={form.variantId} onChange={(e) => setForm({ ...form, variantId: e.target.value })}>
                    <option value="">— pick —</option>
                    {cands.variants.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </Form.Select>
                </div>
              )}
              <div className="col-md-2"><Form.Label className="small mb-0">Sell rate</Form.Label><Form.Control size="sm" type="number" step="0.01" value={form.sellRateOverride} onChange={(e) => setForm({ ...form, sellRateOverride: e.target.value })} placeholder="auto" /></div>
              <div className="col-md-2"><Form.Label className="small mb-0">HSN (6 digits)</Form.Label><Form.Control size="sm" value={form.hsnCode} onChange={(e) => setForm({ ...form, hsnCode: e.target.value })} /></div>
              <div className="col-md-1"><Form.Label className="small mb-0">GST %</Form.Label><Form.Control size="sm" type="number" value={form.gstPct} onChange={(e) => setForm({ ...form, gstPct: e.target.value })} /></div>
              <div className="col-md-2"><Form.Check type="switch" id={`sep-${line.id}`} label="Separate charge" checked={form.isSeparateCharge} onChange={(e) => setForm({ ...form, isSeparateCharge: e.target.checked })} /></div>
              <div className="col-md-1"><Button size="sm" variant="success" onClick={save}>Save</Button></div>
            </div>
            {line.rateOptions?.length > 1 && <div className="small text-muted mt-2">Other vendors: {line.rateOptions.slice(1).map((r) => `${r.vendorName} ${inr(r.rate)} (${r.effectiveDate})`).join(" · ")}</div>}
          </td>
        </tr>
      )}
    </>
  );
};

const RequirementDesk = () => {
  const { number } = useParams();
  const [d, setD] = useState(null);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try { setD(await getStaffRequirement(number)); } catch (e) { errorToast("Could not load"); }
  }, [number]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await publishQuote(number);
      if (r?.success) { successToast(`Published ${r.data.requirement.quoteNumber}`); setConfirm(false); load(); } else errorToast(r?.message);
    } catch (e) { errorToast(e?.message); } finally { setPublishing(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      const r = await addStaffComment(number, comment, internal ? "internal" : "customer");
      if (r?.success) { setComment(""); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); }
  };
  const waiting = async (value) => {
    try { const r = await setWaitingOn(number, value); if (r?.success) load(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };
  const status = async (customerStatus) => {
    try { const r = await setCustomerStatus(number, customerStatus); if (r?.success) load(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };

  if (!d) return null;
  const r = d.requirement;
  const gaps = d.gaps || {};
  const gapCount = (gaps.unmatched?.length || 0) + (gaps.noCost?.length || 0) + (gaps.noTaxRate?.length || 0);
  const latest = d.versions?.[0];

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 mb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <div className="small"><Link to="/requirements">Requirements</Link> / {r.number}</div>
            <h6 className="fw-bold mb-0">{r.number} · {d.organization ? <Link to={`/organizations/${d.organization.id}`}>{d.organization.name}</Link> : <CrmAccountLink zohoAccountId={r.zohoAccountId}>{r.accountName}</CrmAccountLink>} <span className="badge bg-light text-dark">{r.customerStatus}</span></h6>
            <div className="small text-muted">{r.source} · {when(r.createdAt)}{d.site ? ` · ${d.site.name}` : ""}{r.deliveryDate ? ` · needed by ${r.deliveryDate}` : ""}{d.submittedBy ? ` · by ${d.submittedBy.name} +${d.submittedBy.phone}` : ""}{r.installationWanted ? " · installation" : ""}</div>
            {r.customerNotes && <div className="small">Notes: {r.customerNotes}</div>}
            <div className="small d-flex align-items-center gap-2 mt-1">
              <span className="text-muted">Waiting on</span>
              <Form.Select size="sm" value={r.waitingOn || ""} onChange={(e) => waiting(e.target.value)} style={{ width: 150 }}>
                <option value="">Nobody</option>
                {WAITING_ON.map((w) => <option key={w}>{w}</option>)}
              </Form.Select>
              <WaitingBadge waitingOn={r.waitingOn} waitingSince={r.waitingSince} daysOnly />
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {r.customerStatus === "Submitted" && <Button size="sm" variant="outline-secondary" onClick={() => status("Under Review")}>Mark under review</Button>}
            <Button size="sm" variant="success" onClick={() => setConfirm(true)} disabled={publishing}>{latest ? `Publish revision ${latest.revision + 1}` : "Publish quotation"}</Button>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body p-0">
                <table className="table table-sm mb-0 align-middle">
                  <thead><tr><th>#</th><th>Line</th><th className="text-end">Qty</th><th className="text-end">Cost</th><th className="text-end">Sell</th><th className="text-end">Amount</th><th className="text-end">Margin</th><th>HSN / GST</th><th>Flags</th><th></th></tr></thead>
                  <tbody>{d.lines.map((l) => <LineRow key={l.id} line={l} number={number} onChanged={load} />)}</tbody>
                  <tfoot>
                    <tr className="fw-bold"><td colSpan={5} className="text-end">Taxable {inr(r.quoteTaxable)} + GST {inr(r.quoteGst)}</td><td className="text-end">{inr(r.quoteTotal)}</td><td colSpan={4} className="small fw-normal text-muted">{gapCount ? `${gapCount} line(s) need attention before publishing` : "All lines priced"}</td></tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <div className="col-12">
            <VendorCoveragePanel number={number} />
          </div>

          <div className="col-12">
            <SourcingPanel number={number} requirementId={r.id} onChanged={load} />
          </div>

          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h6 className="fw-bold">Quotation versions</h6>
                {d.versions?.length ? d.versions.map((v) => (
                  <div key={v.id} className="border-bottom py-2 d-flex justify-content-between">
                    <div><b>{v.quoteNumber}</b> <span className="badge bg-light text-dark">{v.status}</span><div className="small text-muted">{v.quoteDate} → {v.validTill} · {v.lines.length} lines · {inr(v.totals.grand)}</div>{v.decisionNote && <div className="small">"{v.decisionNote}"</div>}</div>
                  </div>
                )) : <div className="small text-muted">Not published yet.</div>}
                {d.order && <div className="mt-2">Order <Link to={`/orders/${d.order.id}`}>{d.order.orderNumber}</Link> · {d.order.status}</div>}
                {r.quoteSheetUrl && <div className="small mt-2"><a href={r.quoteSheetUrl} target="_blank" rel="noreferrer">Costing sheet</a></div>}
              </div>
            </div>
            {d.documents?.length > 0 && (
              <div className="card mt-3"><div className="card-body"><h6 className="fw-bold">Attachments</h6>{d.documents.map((doc) => <div key={doc.id}><a href={doc.url} target="_blank" rel="noreferrer">{doc.fileName}</a> <span className="small text-muted">{doc.kind}</span></div>)}</div></div>
            )}
            {r.originalText && <div className="card mt-3"><div className="card-body"><h6 className="fw-bold">As received</h6><pre className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>{r.originalText}</pre></div></div>}
          </div>

          <div className="col-lg-6">
            <div className="card">
              <div className="card-body">
                <h6 className="fw-bold">Conversation</h6>
                {d.comments?.length ? d.comments.map((c) => (
                  <div key={c.id} className={`border-bottom py-2 ${c.visibility === "internal" ? "bg-light px-2" : ""}`}>
                    <div className="small text-muted">{c.authorSide === "customer" ? `${c.authorName || "Customer"} (customer)` : c.authorName || "ManBhawan"}{c.visibility === "internal" ? " · internal" : ""} · {when(c.createdAt)}</div>
                    <div>{c.text}</div>
                  </div>
                )) : <div className="small text-muted">No messages.</div>}
                {d.organization && (
                  <Form onSubmit={send} className="mt-2">
                    <Form.Control as="textarea" rows={2} placeholder="Message to the customer, or an internal note" value={comment} onChange={(e) => setComment(e.target.value)} />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <Form.Check type="switch" id="internal" label="Internal note" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
                      <Button type="submit" size="sm" variant="outline-success">Send</Button>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={confirm} onHide={() => setConfirm(false)}>
        <Modal.Header closeButton><Modal.Title>Publish {latest ? `revision ${latest.revision + 1}` : "quotation"}?</Modal.Title></Modal.Header>
        <Modal.Body>
          <div>Grand total <b>{inr(r.quoteTotal)}</b> incl. GST on {d.lines.filter((l) => l.sellRate !== null).length} priced lines.</div>
          {gapCount > 0 && <div className="text-warning mt-2">{gapCount} line(s) are unpriced or unmapped and will be left off the quotation.</div>}
          {d.organization && <div className="small text-muted mt-2">The customer's approvers will be notified.</div>}
        </Modal.Body>
        <Modal.Footer><Button variant="success" onClick={publish} disabled={publishing}>{publishing ? "Publishing…" : "Publish"}</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default RequirementDesk;
